import type { SampleDataThemeId } from "@repo/shared/sample-data/themes";
import { toast } from "sonner";

import { trpc } from "~/libs/trpc.lib";
import { user$ } from "~/stores/auth";
import { activeOrganization$ } from "~/stores/organizations";
import type { OrgOnboardingStep, UserOnboardingStep } from "./onboarding.types";

/**
 * Result of provisioning a sample-data datasource: the new datasource's id
 * and the theme that was chosen for it (either explicitly or by the
 * server-side theme selector agent).
 */
interface SampleProvisioningResult {
  datasourceId: string;
  themeId: SampleDataThemeId;
}

/**
 * Module-scoped holder for the in-flight `useSampleDatasource` mutation.
 * The datasource step kicks this off and navigates away immediately; the
 * sample screen then picks up the same promise to render its loading state
 * and finish the flow. Resets on page reload, which keeps refresh-mid-flow
 * safe (the aborted fetch never creates a datasource).
 */
let sampleProvisioningPromise: Promise<SampleProvisioningResult> | null = null;

/**
 * Begin provisioning a sample-data datasource (theme selection + creation
 * happen server-side). Idempotent: repeated calls return the same in-flight
 * promise so the mutation fires exactly once.
 */
export const startSampleProvisioning =
  (): Promise<SampleProvisioningResult> => {
    if (!sampleProvisioningPromise) {
      sampleProvisioningPromise = trpc.onboarding.useSampleDatasource.mutate(
        {},
      );
    }
    return sampleProvisioningPromise;
  };

/**
 * Return the in-flight sample provisioning promise, or `null` if none has
 * been started (e.g. a fresh page load or a resume navigation).
 */
export const getSampleProvisioning =
  (): Promise<SampleProvisioningResult> | null => sampleProvisioningPromise;

/** Clear the cached sample provisioning promise once the flow is done. */
export const clearSampleProvisioning = () => {
  sampleProvisioningPromise = null;
};

/**
 * Advance the user's personal onboarding step. Server-first: the mutation
 * commits in Postgres before `user$.onboarding` moves locally, so the
 * `_app` gate (which reads the server-injected `context.user.onboarding`
 * on next navigation) can never see the client ahead of the server.
 */
export const setUserOnboardingStep = async (step: UserOnboardingStep) => {
  try {
    await trpc.onboarding.setUserStep.mutate({ step });
    user$.onboarding.set(step);
  } catch (err) {
    toast.error("Failed to save onboarding progress");
    throw err;
  }
};

/**
 * Advance the active org's onboarding step. Server-first to keep `org`
 * truth in Postgres, with an optimistic local update on `activeOrganization$`
 * so the layout observer and onboard guards see the new value immediately.
 * `appDataReady$` reads `activeOrganization$.onboarding`, so the optimistic
 * update is what lets CRUD stores fire `*.list` queries right after the
 * datasource step without waiting for better-auth's atom to refetch.
 */
export const setOrgOnboardingStep = async (step: OrgOnboardingStep) => {
  try {
    await trpc.onboarding.setOrgStep.mutate({ step });
    activeOrganization$.onboarding.set(step);
  } catch (err) {
    toast.error("Failed to save onboarding progress");
    throw err;
  }
};

/**
 * Save the user's job role and (optional) IANA timezone during onboarding.
 * Server-first to keep behavior consistent with `setUserOnboardingStep`.
 */
export const setOnboardingProfile = async (input: {
  role: string;
  timezone?: string;
}) => {
  try {
    await trpc.onboarding.setProfile.mutate(input);
    user$.role.set(input.role);
    if (input.timezone) user$.timezone.set(input.timezone);
  } catch (err) {
    toast.error("Failed to save profile");
    throw err;
  }
};

/**
 * Save company details on the active org during onboarding. The server also
 * advances `org.onboarding` from `"company"` to `"datasource"` in the same
 * call, so we mirror that locally to keep subsequent guards in sync.
 * Payment now happens at the end of the wizard, not right after the
 * company step.
 */
export const setOnboardingCompany = async (data: {
  website?: string;
  employeeCount?: string;
  currentTool?: string;
}) => {
  try {
    await trpc.onboarding.setCompany.mutate(data);
    activeOrganization$.onboarding.set("datasource");
  } catch (err) {
    toast.error("Failed to save company details");
    throw err;
  }
};
